import { generateClient } from 'aws-amplify/api';
import { type GraphQLResult } from '@aws-amplify/api';

const client = generateClient();

// GraphQL queries and mutations
const GET_ACCESS_REQUEST_BY_EMAIL = `
  query AccessRequestByEmail($email: String!) {
    accessRequestByEmail(email: $email) {
      items {
        id
        email
        description
        status
        adminNotes
        owner
        createdAt
        updatedAt
      }
    }
  }
`;

const CREATE_ACCESS_REQUEST = `
  mutation CreateAccessRequest($input: CreateAccessRequestInput!) {
    createAccessRequest(input: $input) {
      id
      email
      description
      status
      owner
      createdAt
      updatedAt
    }
  }
`;

const UPDATE_ACCESS_REQUEST = `
  mutation UpdateAccessRequest($input: UpdateAccessRequestInput!) {
    updateAccessRequest(input: $input) {
      id
      email
      description
      status
      adminNotes
      owner
      createdAt
      updatedAt
    }
  }
`;

const LIST_ACCESS_REQUESTS = `
  query ListAccessRequests($filter: ModelAccessRequestFilterInput, $limit: Int, $nextToken: String) {
    listAccessRequests(filter: $filter, limit: $limit, nextToken: $nextToken) {
      items {
        id
        email
        description
        status
        adminNotes
        owner
        createdAt
        updatedAt
      }
      nextToken
    }
  }
`;

// Types
export type AccessRequestStatus = 'PENDING' | 'APPROVED' | 'DENIED';

export interface AccessRequest {
  id: string;
  email: string;
  description: string;
  status: AccessRequestStatus;
  adminNotes?: string;
  owner?: string;
  createdAt: string;
  updatedAt: string;
}

export interface CreateAccessRequestInput {
  email: string;
  description: string;
}

export interface UpdateAccessRequestInput {
  id: string;
  description?: string;
  status?: AccessRequestStatus;
  adminNotes?: string;
}

// ---------------------------------------------------------------------------
// AI Analyst (chat) access requests
// ---------------------------------------------------------------------------

export const AI_ANALYST_REQUEST_PREFIX = "[AI Analyst]";

export function buildAiAnalystRequestDescription(args: {
  organizationId: string;
  organizationName?: string;
  userName?: string;
  email: string;
}): string {
  const { organizationId, organizationName, userName, email } = args;
  const who = userName ? `${userName} (${email})` : email;
  const orgLabel = organizationName ? `"${organizationName}" (org:${organizationId})` : `org:${organizationId}`;
  return `${AI_ANALYST_REQUEST_PREFIX} ${who} requested AI Analyst access for organization ${orgLabel}`;
}

export function isAiAnalystRequest(request: AccessRequest): boolean {
  return request?.description?.startsWith(AI_ANALYST_REQUEST_PREFIX) ?? false;
}

/** Extract the organization id from a tagged AI Analyst request description. */
export function getAiAnalystRequestOrgId(request: AccessRequest): string | null {
  const description = request?.description;
  if (!description) return null;
  const regex = /org:([^\s)"]+)/g;
  let lastId: string | null = null;
  let match: RegExpExecArray | null;
  while ((match = regex.exec(description)) !== null) {
    lastId = match[1];
  }
  return lastId;
}

// Service class
export class AccessRequestService {
  // Get access request by email (returns first match; a user may now have more
  // than one — see getAccessRequestsByEmail for the full list)
  static async getAccessRequestByEmail(email: string): Promise<AccessRequest | null> {
    const items = await this.getAccessRequestsByEmail(email);
    return items[0] ?? null;
  }

  // Get all access requests for an email (a user can have e.g. a platform request and an AI Analyst request)
  static async getAccessRequestsByEmail(email: string): Promise<AccessRequest[]> {
    try {
      const result = await client.graphql({
        query: GET_ACCESS_REQUEST_BY_EMAIL,
        variables: { email }
      }) as GraphQLResult<{ accessRequestByEmail: { items: AccessRequest[] } }>;

      return result.data?.accessRequestByEmail?.items || [];
    } catch (error) {
      console.error('Error fetching access requests by email:', error);
      return [];
    }
  }

  // Create a new access request
  static async createAccessRequest(input: CreateAccessRequestInput): Promise<AccessRequest> {
    const result = await client.graphql({
      query: CREATE_ACCESS_REQUEST,
      variables: {
        input: {
          email: input.email,
          description: input.description,
          status: 'PENDING',
        }
      }
    }) as GraphQLResult<{ createAccessRequest: AccessRequest }>;

    if (!result.data?.createAccessRequest) {
      throw new Error('Failed to create access request');
    }

    return result.data.createAccessRequest;
  }

  // Update an existing access request
  static async updateAccessRequest(input: UpdateAccessRequestInput): Promise<AccessRequest> {
    const result = await client.graphql({
      query: UPDATE_ACCESS_REQUEST,
      variables: { input }
    }) as GraphQLResult<{ updateAccessRequest: AccessRequest }>;

    if (!result.data?.updateAccessRequest) {
      throw new Error('Failed to update access request');
    }

    return result.data.updateAccessRequest;
  }

  // List all access requests (Admin only). Pages through nextToken so results
  // aren't silently truncated; `limit` is the per-page size, not a hard cap.
  static async listAccessRequests(limit: number = 100): Promise<AccessRequest[]> {
    try {
      const items: AccessRequest[] = [];
      let nextToken: string | undefined = undefined;

      do {
        const result = await client.graphql({
          query: LIST_ACCESS_REQUESTS,
          variables: { limit, nextToken }
        }) as GraphQLResult<{ listAccessRequests: { items: AccessRequest[]; nextToken?: string } }>;

        items.push(...(result.data?.listAccessRequests?.items || []));
        nextToken = result.data?.listAccessRequests?.nextToken ?? undefined;
      } while (nextToken);

      return items;
    } catch (error) {
      console.error('Error listing access requests:', error);
      return [];
    }
  }
}
